import {Component, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {ErrorModel} from '../../../app.model';
import {selectUserIsAdmin} from '../../../user/user.reducer';
import {SegmentGlobalToggleComponent} from '../../global-toggle/global-toggle.component';
import {Segment} from '../../segment.model';
import {SubSink} from 'subsink';
import {ActionsSubject, Store} from '@ngrx/store';
import {Observable} from 'rxjs';
import {
  editSegment,
  segmentEdited,
  segmentEditError,
  SegmentWithAccount,
  selectSegmentDetails
} from '../../segments.state';
import {ofType} from '@ngrx/effects';
import {SegmentCreationData} from '../create/create.component';
import {ApiVersion} from '../../../app.types';
import {first, map, startWith} from 'rxjs/operators';
import {
  selectCategories,
  selectSegmentAudienceTypes,
  selectSegmentMainTypes,
  selectSegmentSubTypes
} from '../../../filters-config/filters-config.selectors';

@Component({
  selector: 'app-segment-edit',
  template: `
    <form [formGroup]="segmentEditForm"
          (submit)="submit()"
          id="segment-edit-form"
          data-cy="element-segment-edit-form">
      <button mat-icon-button
              id="button-dialog-close"
              class="mat-icon-button-small"
              data-cy="button-dialog-close"
              mat-dialog-close>
        <mat-icon>clear</mat-icon>
      </button>
      <h5 mat-dialog-title>Edit Segment</h5>
      <div mat-dialog-content class="mat-body-1">
        <p>Edit the segment name and description. <span *ngIf="isAdmin$ | async">If you want to share this segment with
      other profiles, check the "global" checkbox.</span></p>
        <p [hidden]="!errorMessage" class="error" id="error-feedback">{{ errorMessage }}</p>
        <p *ngIf="showSharedMessage" class="warning" id="error-shared">You're changing the name of a shared
          segment.
          This change will be pushed to all the platforms that the segment is shared with. Depending on number
          of platforms - it may take long time to complete. Any errors will be shown in audit-log of this segment.
        </p>
        <mat-form-field id="segment-edit-name-input">
          <input #segmentName
                 formControlName="name"
                 class="form-input"
                 id="segment-name"
                 matInput
                 maxlength="500"
                 name="segmentName"
                 placeholder="Segment Name">
          <mat-hint align="end">{{ segmentName.value.length }} / 500</mat-hint>
          <mat-error *ngIf="segmentEditForm.get('name')?.hasError('required')">
            Segment name is empty
          </mat-error>
        </mat-form-field>
        <mat-form-field>
          <mat-label>Main Type</mat-label>
          <input formControlName="mainType"
                 matInput
                 appClearSpacesInAutocomplete
                 [matAutocomplete]="autoCompleteMainType"
                 data-cy="segment-type-control"
          />
          <mat-autocomplete #autoCompleteMainType="matAutocomplete">
            <mat-option *ngFor="let option of filteredSegmentMainTypesOptions | async" [value]="option"
                        data-cy="segment-type-control-select-option">
              {{ option }}
            </mat-option>
          </mat-autocomplete>
        </mat-form-field>

        <mat-form-field>
          <mat-label>Sub Type</mat-label>
          <input formControlName="subType"
                 matInput
                 appClearSpacesInAutocomplete
                 [matAutocomplete]="autoCompleteSubType"
          />
          <mat-autocomplete #autoCompleteSubType="matAutocomplete">
            <mat-option *ngFor="let option of filteredSegmentSubTypesOptions | async" [value]="option">
              {{ option }}
            </mat-option>
          </mat-autocomplete>
        </mat-form-field>

        <mat-form-field>
          <mat-label>Audience Type</mat-label>
          <input formControlName="audienceType"
                 matInput
                 appClearSpacesInAutocomplete
                 [matAutocomplete]="autoCompleteAudienceType"
          />
          <mat-autocomplete #autoCompleteAudienceType="matAutocomplete">
            <mat-option *ngFor="let option of filteredSegmentAudienceTypesOptions | async" [value]="option">
              {{ option }}
            </mat-option>
          </mat-autocomplete>
        </mat-form-field>
        <mat-form-field>
          <input formControlName="category" matInput appClearSpacesInAutocomplete
                 placeholder="Category"
                 [matAutocomplete]="autoCategory"
                 id="segment-category"/>
          <mat-autocomplete #autoCategory="matAutocomplete">
            <mat-option *ngFor="let option of filteredCategoryOptions | async"
                        [value]="option">
              {{ option }}
            </mat-option>
          </mat-autocomplete>
          <mat-error *ngIf="categoryControl.hasError('required')">
            Category is empty
          </mat-error>
          <mat-error
            *ngIf="!categoryControl.hasError('required') && categoryControl.hasError('unknownCategory')">
            Only existing categories can be selected
          </mat-error>
        </mat-form-field>
        <ng-container *ngIf="parentSegmentControl.enabled">
          <p>If there's a relation in segment-config between segments, you
            can select a 'parent' segment
            here that the current segment is dependant on</p>
          <app-parent-segment [parentSegmentControl]="parentSegmentControl"
                              data-cy="parent-control">
          </app-parent-segment>
        </ng-container>
        <ng-container *ngIf="taxonomyControl.enabled">
          <p>Here you can change taxonomy from segment level</p>
          <app-taxonomy-dropdown [taxonomyControl]="taxonomyControl"
                                 data-cy="taxonomy-control">
          </app-taxonomy-dropdown>
        </ng-container>
        <mat-form-field>
                  <textarea #segmentDescription
                            formControlName="description"
                            class="form-input"
                            id="segment-description"
                            matInput
                            maxlength="500"
                            name="segmentDescription"
                            placeholder="Segment Description"
                            rows="4"></textarea>
          <mat-hint align="end">{{ segmentDescription.value.length }} / 500</mat-hint>
        </mat-form-field>

        <app-keyword-chips *ngIf="segment.keywords" [tags]="segment.keywords"
                           (tagsChange)="handleKeywordsChange($event)"></app-keyword-chips>

        <app-segment-global-toggle #globalSegmentToggle [segment]="segment"
                                   id="current-partner"></app-segment-global-toggle>
      </div>
      <div mat-dialog-actions>
        <button (click)="reset(); segment.keywords=''" mat-raised-button type="reset">Reset</button>
        <button type="submit"
                color="primary"
                id="button-segment-edit"
                data-cy="button-segment-edit"
                mat-raised-button>Save
        </button>
      </div>
    </form>
  `
})
export class SegmentEditComponent implements OnInit, OnDestroy {

  @ViewChild('globalSegmentToggle') globalSegmentToggle!: SegmentGlobalToggleComponent;
  errorMessage!: string;
  selectedSegment$: Observable<SegmentWithAccount | undefined> = this.store.select(selectSegmentDetails);
  // form data model
  segment!: Segment;
  segmentOriginalName!: string;
  showSharedMessage = false;
  isAdmin$: Observable<boolean> = this.store.select(selectUserIsAdmin);
  subSink = new SubSink();
  parentSegmentControl = new FormControl({disabled: true});
  taxonomyControl = new FormControl({disabled: true});
  categoryOptions: string[] = [];
  categoryControl = new FormControl({value: ''});
  mainTypeControl = new FormControl();
  subTypeControl = new FormControl();
  audienceTypeControl = new FormControl();
  filteredCategoryOptions = this.categoryControl.valueChanges.pipe(
    startWith(''),
    map((value: string) => {
      const filterValue = value?.toLowerCase();
      return this.categoryOptions.filter(option => option.toLowerCase().includes(filterValue));
    })
  );
  segmentMainTypes: string[] = [];
  filteredSegmentMainTypesOptions = this.mainTypeControl.valueChanges.pipe(
    startWith(''),
    map((value: string) => {
      const filterValue = value?.toLowerCase();
      return this.segmentMainTypes.filter(option => option.toLowerCase().includes(filterValue));
    })
  );
  segmentSubTypes: string[] = [];
  filteredSegmentSubTypesOptions = this.subTypeControl.valueChanges.pipe(
    startWith(''),
    map((value: string) => {
      const filterValue = value?.toLowerCase();
      return this.segmentSubTypes.filter(option => option.toLowerCase().includes(filterValue));
    })
  );
  segmentAudienceTypes: string[] = [];
  filteredSegmentAudienceTypesOptions = this.audienceTypeControl.valueChanges.pipe(
    startWith(''),
    map((value: string) => {
      const filterValue = value?.toLowerCase();
      return this.segmentAudienceTypes.filter(option => option.toLowerCase().includes(filterValue));
    })
  );
  private keywordsControl = new FormControl('');
  segmentEditForm: FormGroup = new FormGroup({
    id: new FormControl(''),
    name: new FormControl('', Validators.required),
    description: new FormControl(''),
    partnerId: new FormControl(''),
    keywords: this.keywordsControl,
    parent: this.parentSegmentControl,
    taxonomy: this.taxonomyControl,
    category: this.categoryControl,
    mainType: this.mainTypeControl,
    subType: this.subTypeControl,
    audienceType: this.audienceTypeControl
  });

  constructor(private store: Store,
              private actions: ActionsSubject,
              @Inject(MAT_DIALOG_DATA) public data: SegmentCreationData,
              private dialogRef: MatDialogRef<SegmentEditComponent>) {
  }

  ngOnInit() {
    // weird, angular doesnt cleanup template after modal is closed
    this.clear();
    if (this.data.apiVersion === ApiVersion.V1) {
      this.parentSegmentControl.disable();
      this.taxonomyControl.disable();
    } else {
      this.parentSegmentControl.enable();
      this.taxonomyControl.enable();
    }
    this.store.select(selectCategories).pipe(first()).subscribe(val => this.categoryOptions = val);
    this.store.select(selectSegmentMainTypes).pipe(first()).subscribe(val => this.segmentMainTypes = val);
    this.store.select(selectSegmentSubTypes).pipe(first()).subscribe(val => this.segmentSubTypes = val);
    this.store.select(selectSegmentAudienceTypes).pipe(first()).subscribe(val => this.segmentAudienceTypes = val);

    this.subSink.sink = this.selectedSegment$
      .subscribe(seg => {
        this.segmentOriginalName = seg?.segment.name || '';
        this.segment = {...(seg?.segment || {} as Segment)};
        this.reset();
        this.parentSegmentControl.setValue(this.segment.parentId);
        this.taxonomyControl.setValue(this.segment.taxonomyId);
      });
    this.subSink.sink = this.actions.pipe(ofType(segmentEditError))
      .subscribe(action => {
        this.displayError(action.error);
      });
    this.subSink.sink = this.actions.pipe(ofType(segmentEdited))
      .subscribe(_ => {
        this.dialogRef.close();
      });
    this.subSink.sink = this.segmentEditForm.controls['name'].valueChanges?.subscribe(change => this.nameChange(change));
  }

  nameChange(newValue: string) {
    if ((this.segment.sharedAccounts || []).length === 0) {
      return;
    }
    this.showSharedMessage = newValue !== this.segmentOriginalName;
  }

  ngOnDestroy() {
    this.subSink.unsubscribe();
  }

  clear() {
    this.errorMessage = '';
  }

  reset() {
    this.clear();
    this.segmentEditForm.reset(this.segment);
  }

  submit() {
    if (this.segmentEditForm.valid) {
      this.store.dispatch(
        editSegment({
          segment: this.segmentEditForm.value as Segment,
          makeGlobal: this.globalSegmentToggle.toggle && this.globalSegmentToggle.toggle.checked,
          apiVersion: this.data.apiVersion
        }));
    }
  }

  public handleKeywordsChange(e: string) {
    this.keywordsControl.setValue(e);
  }

  private displayError(error: ErrorModel) {
    const text = error.message as string;
    if (text.toLowerCase().indexOf('name not unique') >= 0) {
      this.errorMessage = 'The segment name must be unique';
    } else if (text.toLowerCase().indexOf('not found') >= 0) {
      this.errorMessage = 'The segment does not exist';
    } else if (text.toLowerCase().indexOf('unauthorized') >= 0) {
      this.errorMessage = 'Update not allowed';
    } else if (error.status >= 400) {
      this.errorMessage = 'Error: ' + text;
    } else {
      this.errorMessage = `oops ¯\\_(ツ)_/¯ it appears something may have gone wrong`;
      console.error(error);
    }
  }

}
